// Lib
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { isString } from 'lodash';

// Selectors
import { getCurrentVisibleBoardError, getIsCurrentVisibleBoardFetched } from '../../element/board/boardSelector';
import { getActiveErrorNoticeId, getErrorNoticeData } from './errorNoticeSelector';
import { getCurrentUser } from '../../user/currentUserSelector';
import { getIsCurrentBoardRestored } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getPreviousViewedBoardId } from '../../user/navigationHistory/navigationHistorySelector';
import { getMilanoteApplicationModeSelector } from '../../platform/platformSelector';

// Utils
import { isNetworkError } from '../../utils/errorUtil';
import { prop } from '../../../common/utils/immutableHelper';
import { manuallyReportError } from '../../analytics/rollbarService';
import { isBoardAccessDeniedError, isTransientBoardFetchError } from '../../element/board/boardErrorUtils';
import { getIsGuestProp, getRootBoardId } from '../../../common/users/utils/userPropertyUtils';
import workspaceInitialisationMonitoringSingleton from '../../workspace/initialisation/workspaceInitialisationMonitoringSingleton';
import usePrevious from '../../utils/react/usePrevious';
import getErrorNoticeContent from './errorComponents/getErrorNoticeContent';
import { getQuickNotesRootBoardId } from '../../../common/users/userHelper';

// Actions
import { navigateToBoard, navigateToLogin } from '../../reducers/navigationActions';
import { closeErrorNotice, openErrorNotice } from './errorNoticeActions';
import { currentBoardIdSet } from '../../reducers/currentBoardId/currentBoardIdActions';

// Components
import MobileErrorSheet from './sheet/MobileErrorSheet';
import DesktopErrorModal from './modals/DesktopErrorModal';

// Constants
import { BOARD_ERROR } from '../../../common/error/errorConstants';
import { ROLLBAR_LEVELS } from '../../analytics/rollbarConstants';
import { MilanoteApplicationMode } from '../../../common/platform/platformTypes';

const fetchedCurrentBoardErrorSelector = createSelector(
    getCurrentVisibleBoardError,
    getIsCurrentVisibleBoardFetched,
    (currentBoardError, isCurrentBoardFetched) =>
        // If we don't have the board then we need to wait for it before displaying an error
        isCurrentBoardFetched ? currentBoardError : null,
);

type Props = {
    currentBoardId: string;
    location: Location;
};

const WorkspaceErrorNoticeManager: React.FC<Props> = (props: Props) => {
    const { currentBoardId, location } = props;

    const currentUser = useSelector(getCurrentUser);
    const previousBoardId = useSelector(getPreviousViewedBoardId);
    const isCurrentBoardRestored = useSelector(getIsCurrentBoardRestored);
    const isMobile = useSelector(getMilanoteApplicationModeSelector) === MilanoteApplicationMode.mobile;

    const fetchedCurrentBoardError = useSelector(fetchedCurrentBoardErrorSelector);
    const activeErrorNoticeId = useSelector(getActiveErrorNoticeId);
    const errorNoticeData = useSelector(getErrorNoticeData);

    const errorNoticeContent = getErrorNoticeContent(activeErrorNoticeId);

    const dispatch = useDispatch();

    const prevFetchCurrentBoardError = usePrevious(fetchedCurrentBoardError);
    const prevLocation = usePrevious<Location>(location);

    const viewPreviousOrRootBoard = (errorDetails: any) => {
        const rootBoardId = getRootBoardId(currentUser);
        const isGuest = getIsGuestProp(currentUser);

        if (isGuest) return;

        if (currentBoardId !== rootBoardId) {
            let boardId = previousBoardId && previousBoardId !== currentBoardId ? previousBoardId : rootBoardId;

            // Only check for quickNotesBoardId on mobile as the user can't navigate to it on desktop
            // to avoid a redirection loop betwen the deleted board and the quick notes sheet
            if (isMobile) {
                const quickNotesBoardId = getQuickNotesRootBoardId(currentUser);
                if (previousBoardId === quickNotesBoardId) {
                    boardId = rootBoardId;
                }
            }

            return dispatch(currentBoardIdSet({ boardId }));
        }

        if (!isNetworkError(errorDetails)) {
            const errorMessage = 'The root board has an error, this cannot be recovered from.';
            const error = { id: errorDetails.id, rootBoardId, errorMessage, details: errorDetails };
            manuallyReportError({ errorMessage, error, sensitive: false, level: ROLLBAR_LEVELS.CRITICAL });
            console.error(errorMessage, rootBoardId, errorDetails);
        }
    };

    const displayWorkspaceError = () => {
        let error = fetchedCurrentBoardError;
        if (!error) return null;

        if (isString(error)) {
            error = {
                code: BOARD_ERROR.GENERIC_ERROR,
                message: error,
            };
        }

        const errorNoticeId = prop('code', error) || BOARD_ERROR.GENERIC_ERROR;

        // Password protected board errors should not result in an error notice
        if (errorNoticeId === BOARD_ERROR.PASSWORD_PROTECTED) return;

        dispatch(
            openErrorNotice({
                errorNoticeId: errorNoticeId,
                data: {
                    error,
                    boardId: currentBoardId,
                    isRestored: isCurrentBoardRestored,
                },
            }),
        );

        // If an error is shown during initialisation, don't track the page load times
        workspaceInitialisationMonitoringSingleton.cancelTimer();

        viewPreviousOrRootBoard(error);
        return error;
    };

    const closeErrorNoticeAndNavigate = () => {
        const isGuest = getIsGuestProp(currentUser);
        const shouldNavigateToLogin = isGuest && !isTransientBoardFetchError(fetchedCurrentBoardError);
        if (shouldNavigateToLogin) {
            dispatch(navigateToLogin());
            return;
        }

        dispatch(closeErrorNotice());
        dispatch(navigateToBoard({ boardId: currentBoardId, trackTiming: false }));
    };

    useEffect(() => {
        // A guest user has a permissions error, so just redirect them to login instead of showing the error message
        if (isBoardAccessDeniedError(fetchedCurrentBoardError) && getIsGuestProp(currentUser)) {
            dispatch(navigateToLogin({ from: location.pathname }));
        }

        // If there's an error notice active, close it on route change
        if (activeErrorNoticeId && prevLocation?.pathname !== location?.pathname) {
            dispatch(closeErrorNotice());
        }

        if (fetchedCurrentBoardError && prevFetchCurrentBoardError !== fetchedCurrentBoardError) {
            displayWorkspaceError();
        }
    }, [fetchedCurrentBoardError, currentUser, location, activeErrorNoticeId]);

    useEffect(() => {
        return () => {
            if (activeErrorNoticeId) dispatch(closeErrorNotice());
        };
    }, []);

    //
    const ErrorNoticeComponent = isMobile ? MobileErrorSheet : DesktopErrorModal;

    return (
        <ErrorNoticeComponent
            boardId={currentBoardId}
            errorNoticeData={errorNoticeData}
            errorNoticeContent={errorNoticeContent}
            close={closeErrorNoticeAndNavigate}
        />
    );
};

export default WorkspaceErrorNoticeManager;
